"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LookUpField = exports.NOT_FORMAT_FUNC_SET = exports.LOOKUP_VALUE_FUNC_SET = exports.ORIGIN_VALUES_FUNC_SET = void 0;
const compute_manager_1 = require("../../compute_manager");
const evaluate_1 = require("../../formula_parser/evaluate");
const consts_1 = require("../../formula_parser/consts");
const functions_1 = require("../../formula_parser/functions");
const i18n_1 = require("../../exports/i18n");
const joi_1 = __importDefault(require("joi"));
const lodash_1 = require("lodash");
const constants_1 = require("../../model/constants");
const utils_1 = require("../../model/utils");
const utils_2 = require("../../utils");
const env_1 = require("../../utils/env");
const store_1 = require("../../exports/store");
const selectors_1 = require("../../exports/store/selectors");
const field_types_1 = require("../../types/field_types");
const view_types_1 = require("../../types/view_types");
const checkbox_field_1 = require("./checkbox_field");
const date_time_base_field_1 = require("./date_time_base_field");
const field_1 = require("./field");
const number_base_field_1 = require("./number_base_field");
const stat_1 = require("./stat");
const text_base_field_1 = require("./text_base_field");
const validate_schema_1 = require("./validate_schema");
const view_filter_derivate_1 = require("../../compute_manager/view_derivate/slice/view_filter_derivate");
// function that needs to output as-is
exports.ORIGIN_VALUES_FUNC_SET = new Set([
    field_types_1.RollUpFuncType.VALUES,
    field_types_1.RollUpFuncType.ARRAYUNIQUE,
    field_types_1.RollUpFuncType.ARRAYCOMPACT,
]);
// Functions that need to be displayed by string concatenation
exports.LOOKUP_VALUE_FUNC_SET = new Set([
    field_types_1.RollUpFuncType.ARRAYJOIN,
    field_types_1.RollUpFuncType.CONCATENATE,
]);
// Functions that don't require number formatting
exports.NOT_FORMAT_FUNC_SET = new Set([
    field_types_1.RollUpFuncType.ARRAYJOIN,
    field_types_1.RollUpFuncType.CONCATENATE,
    field_types_1.RollUpFuncType.ARRAYUNIQUE,
    field_types_1.RollUpFuncType.ARRAYCOMPACT,
]);
class LookUpField extends field_1.ArrayValueField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.state = state;
    }
    validateProperty() {
        return LookUpField.propertySchema.validate(this.field.property);
    }
    validateCellValue() {
        return (0, validate_schema_1.joiErrorResult)("computed field shouldn't validate cellValue");
    }
    validateOpenWriteValue() {
        return (0, validate_schema_1.joiErrorResult)("computed field shouldn't validate cellValue");
    }
    get apiMetaPropertyFormat() {
        return (0, utils_1.getApiMetaPropertyFormat)(this);
    }
    get apiMetaProperty() {
        const res = {
            relatedLinkFieldId: this.field.property.relatedLinkFieldId,
            targetFieldId: this.field.property.lookUpTargetFieldId,
            rollupFunction: this.rollUpType,
        };
        if (this.hasError) {
            res.hasError = this.hasError;
            return res;
        }
        res.valueType = this.basicValueType;
        const lookUpEntityFieldInfo = this.getLookUpEntityFieldInfo();
        if (lookUpEntityFieldInfo) {
            res.entityField = {
                datasheetId: lookUpEntityFieldInfo.datasheetId,
                field: field_1.Field.bindContext(lookUpEntityFieldInfo.field, this.state).getApiMeta(lookUpEntityFieldInfo.datasheetId)
            };
        }
        if (this.apiMetaPropertyFormat) {
            res.format = this.apiMetaPropertyFormat;
        }
        return res;
    }
    /**
     * Has the value type changed? (Is it still an array of entity field values?)
     * - unchanged: Calculated according to entity field openValue JsonSchema
     * - changed: push down jsonSchema by output value type
     */
    get openValueJsonSchema() {
        const entityField = this.getLookUpEntityField();
        if (!entityField) {
            return {
                type: 'string',
                title: this.field.name,
                disabled: true,
            };
        }
        const expression = this.getExpression();
        if (expression) {
            // When there is an expression, the output value type base type
            switch (this.valueType) {
                case field_types_1.BasicValueType.Number:
                case field_types_1.BasicValueType.Boolean:
                case field_types_1.BasicValueType.DateTime:
                case field_types_1.BasicValueType.String:
                    return {
                        type: constants_1.ValueTypeMap[this.basicValueType],
                        title: this.field.name,
                    };
                default:
                    return {
                        type: 'string',
                        title: this.field.name,
                    };
            }
        }
        if (exports.LOOKUP_VALUE_FUNC_SET.has(this.rollUpType)) {
            return {
                type: 'string',
                title: this.field.name,
            };
        }
        const entityFieldSchema = field_1.Field.bindContext(entityField, this.state).openValueJsonSchema;
        return {
            type: 'array',
            title: this.field.name,
            items: entityFieldSchema
        };
    }
    get canGroup() {
        if (this.hasError) {
            return false;
        }
        const lookUpEntityField = field_1.Field.bindContext(this.field, this.state).getLookUpEntityField();
        if (!lookUpEntityField) {
            return false;
        }
        return field_1.Field.bindContext(lookUpEntityField, this.state).canGroup;
    }
    get hasError() {
        const { datasheetId } = this.field.property;
        if ((0, env_1.isClient)()) {
            const computeRefManager = (0, compute_manager_1.getComputeRefManager)(this.state);
            if (!computeRefManager.checkRef(`${datasheetId}-${this.field.id}`)) {
                return true;
            }
        }
        if (!this.getLookUpEntityField()) {
            return true;
        }
        const { error: isFilterError } = this.checkFilterInfo();
        return isFilterError;
    }
    get warnText() {
        const { typeSwitch: isFilterTypeSwitch } = this.checkFilterInfo();
        return isFilterTypeSwitch ? (0, i18n_1.t)(i18n_1.Strings.lookup_filter_waring) : '';
    }
    // statistic options
    get statTypeList() {
        const expression = this.getExpression();
        const rollUpType = this.field.property.rollUpType;
        if (expression) {
            switch (this.valueType) {
                case field_types_1.BasicValueType.Number:
                    return number_base_field_1.NumberBaseField._statTypeList;
                case field_types_1.BasicValueType.Boolean:
                    return checkbox_field_1.CheckboxField._statTypeList;
                case field_types_1.BasicValueType.DateTime:
                    return date_time_base_field_1.DateTimeBaseField._statTypeList;
                case field_types_1.BasicValueType.String:
                    return text_base_field_1.TextBaseField._statTypeList;
            }
        }
        if (exports.LOOKUP_VALUE_FUNC_SET.has(rollUpType)) {
            return text_base_field_1.TextBaseField._statTypeList;
        }
        const lookUpEntityField = field_1.Field.bindContext(this.field, this.state).getLookUpEntityField();
        if (!lookUpEntityField) {
            return [];
        }
        return field_1.Field.bindContext(lookUpEntityField, this.state).statTypeList;
    }
    get basicValueType() {
        const expression = this.getExpression();
        if (expression) {
            const fieldMap = (0, selectors_1.getFieldMap)(this.state, this.field.property.datasheetId);
            const fExp = (0, evaluate_1.parse)(expression, { field: this.field, fieldMap, state: this.state });
            if (!('error' in fExp)) {
                return fExp.ast.valueType;
            }
        }
        if (exports.LOOKUP_VALUE_FUNC_SET.has(this.rollUpType)) {
            return field_types_1.BasicValueType.String;
        }
        return field_types_1.BasicValueType.Array;
    }
    // When the underlying type is Array, get the underlying type of the elements in the Array
    get innerBasicValueType() {
        const entityField = this.getLookUpEntityField();
        if (!entityField) {
            return field_types_1.BasicValueType.String;
        }
        const valueType = field_1.Field.bindContext(entityField, this.state).basicValueType;
        // The array in the array is still an array, 
        // indicating that it is a multi-select field, and it can be specified as a string directly.
        return valueType === field_types_1.BasicValueType.Array ? field_types_1.BasicValueType.String : valueType;
    }
    static defaultProperty() {
        return {
            datasheetId: '',
            relatedLinkFieldId: '',
            lookUpTargetFieldId: '',
        };
    }
    get isComputed() {
        return true;
    }
    get rollUpType() {
        return this.field.property.rollUpType || field_types_1.RollUpFuncType.VALUES;
    }
    showFOperatorDesc(type) {
        const expression = this.getExpression();
        if (expression) {
            switch (this.valueType) {
                case field_types_1.BasicValueType.Number:
                    return number_base_field_1.NumberBaseField.FOperatorDescMap[type];
                case field_types_1.BasicValueType.Boolean:
                    return view_types_1.FOperatorDescMap[type];
                case field_types_1.BasicValueType.String:
                    return view_types_1.FOperatorDescMap[type];
                case field_types_1.BasicValueType.DateTime:
                    return date_time_base_field_1.DateTimeBaseField.FOperatorDescMap[type];
            }
        }
        const entityField = this.getLookUpEntityField();
        if (!entityField) {
            return [];
        }
        return field_1.Field.bindContext(entityField, this.state).showFOperatorDesc(type);
    }
    // filter options
    get acceptFilterOperators() {
        const expression = this.getExpression();
        if (expression) {
            switch (this.valueType) {
                case field_types_1.BasicValueType.Number:
                    return number_base_field_1.NumberBaseField._acceptFilterOperators;
                case field_types_1.BasicValueType.Boolean:
                    return checkbox_field_1.CheckboxField._acceptFilterOperators;
                case field_types_1.BasicValueType.String:
                    return text_base_field_1.TextBaseField._acceptFilterOperators;
                case field_types_1.BasicValueType.DateTime:
                    return date_time_base_field_1.DateTimeBaseField._acceptFilterOperators;
            }
        }
        const entityField = this.getLookUpEntityField();
        if (!entityField) {
            return [];
        }
        return field_1.Field.bindContext(entityField, this.state).acceptFilterOperators.filter(item => item !== view_types_1.FOperator.IsRepeat);
    }
    getLinkFields() {
        const snapshot = (0, selectors_1.getSnapshot)(this.state, this.field.property.datasheetId);
        const fieldMap = snapshot === null || snapshot === void 0 ? void 0 : snapshot.meta.fieldMap;
        return fieldMap ? Object.values(fieldMap).filter(field => field.type === field_types_1.FieldType.Link) : [];
    }
    getRelatedLinkField() {
        return this.getLinkFields().find(field => field.id === this.field.property.relatedLinkFieldId);
    }
    getLookUpEntityFieldDepth(visitedFields, depth) {
        const lookUpEntityFieldInfo = this.getLookUpEntityFieldInfo(visitedFields, depth);
        if (!lookUpEntityFieldInfo) {
            return 0;
        }
        return lookUpEntityFieldInfo.depth;
    }
    // Allow lookup lookup lookup fields, but there will always be an entity field in the end. 
    // Returns entity fields and the depth of the fields.
    getLookUpEntityFieldInfo(visitedFields, depth) {
        const _visitedFields = visitedFields || new Set();
        const _depth = depth || 0;
        const nextTargetInfo = this.getLookUpTargetFieldAndDatasheet();
        if (!nextTargetInfo) {
            return;
        }
        const { field, datasheetId } = nextTargetInfo;
        if (field && datasheetId && _visitedFields.has(`${datasheetId}-${field.id}`)) {
            throw Error('LookUp Circle!');
        }
        if (field && field.type === field_types_1.FieldType.LookUp) {
            _visitedFields.add(`${datasheetId}-${field.id}`);
            return field_1.Field.bindContext(field, this.state).getLookUpEntityFieldInfo(_visitedFields, _depth + 1);
        }
        return {
            field,
            datasheetId: nextTargetInfo.datasheetId,
            depth: _depth,
        };
    }
    // Allow lookup lookup lookup fields, but there will always be an entity field in the end.
    getLookUpEntityField(visitedFields, depth) {
        try {
            const lookUpEntityFieldInfo = this.getLookUpEntityFieldInfo(visitedFields, depth);
            if (!lookUpEntityFieldInfo) {
                return;
            }
            return lookUpEntityFieldInfo.field;
        }
        catch (error) {
            return;
        }
    }
    // Check whether the column corresponding to the filter condition is deleted, 
    // check whether the column corresponding to the filter condition switches the type
    /**
     * Check whether the column corresponding to the filter condition is deleted
     * Check whether the column corresponding to the filter condition switches the type
     */
    checkFilterInfo() {
        const { filterInfo } = this.field.property;
        let error = false;
        let typeSwitch = false;
        if (filterInfo) {
            const relatedLinkField = this.getRelatedLinkField();
            if (!relatedLinkField) {
                return {
                    error: true,
                    typeSwitch: false
                };
            }
            const { foreignDatasheetId } = relatedLinkField.property;
            const foreignSnapshot = (0, selectors_1.getSnapshot)(this.state, foreignDatasheetId);
            const foreignFieldMap = foreignSnapshot.meta.fieldMap;
            const { conditions } = filterInfo;
            conditions.forEach(c => {
                if (!error) {
                    error = !foreignFieldMap[c.fieldId];
                }
                if (!typeSwitch) {
                    typeSwitch = (0, utils_2.checkTypeSwitch)(c, foreignFieldMap[c.fieldId]);
                }
            });
        }
        return { error, typeSwitch };
    }
    /**
     * Get the information of the search target field, return the field and datasheetId information
     *
     * @param {string} [datasheetId]
     * @returns {({ field: IField; datasheetId: string } | undefined)}
     * @memberof LookUpField
     */
    getLookUpTargetFieldAndDatasheet() {
        const relatedLinkField = this.getRelatedLinkField();
        if (!relatedLinkField) {
            return;
        }
        const { foreignDatasheetId } = relatedLinkField.property;
        const { lookUpTargetFieldId } = this.field.property;
        const foreignSnapshot = (0, selectors_1.getSnapshot)(this.state, foreignDatasheetId);
        if (!foreignSnapshot) {
            return;
        }
        const field = foreignSnapshot.meta.fieldMap[lookUpTargetFieldId];
        return {
            field,
            datasheetId: foreignDatasheetId,
        };
    }
    getLookUpTargetField() {
        const nextTargetFieldInfo = this.getLookUpTargetFieldAndDatasheet();
        if (!nextTargetFieldInfo) {
            return;
        }
        return nextTargetFieldInfo.field;
    }
    getExpression() {
        const rollUpType = this.rollUpType;
        if (!exports.ORIGIN_VALUES_FUNC_SET.has(rollUpType) && !exports.LOOKUP_VALUE_FUNC_SET.has(rollUpType)) {
            const formulaFunc = functions_1.Functions.get(rollUpType);
            if (!formulaFunc) {
                return null;
            }
            return `${formulaFunc.name}(${consts_1.ROLLUP_KEY_WORDS})`;
        }
        return null;
    }
    getCellValue(recordId, withError) {
        const expression = this.getExpression();
        const { datasheetId } = this.field.property;
        if (expression) {
            const snapshot = (0, selectors_1.getSnapshot)(this.state, datasheetId);
            const record = snapshot.recordMap[recordId];
            return (0, evaluate_1.evaluate)(expression, { field: this.field, record, state: this.state }, withError);
        }
        const entityField = this.getLookUpEntityField();
        if (!entityField) {
            return null;
        }
        const flatCellValue = this.getFlatCellValue(recordId, true); // The underlying cv value should remain empty.
        if (Array.isArray(flatCellValue) && flatCellValue.length) {
            // Fields of text type store all character fragments, flat needs to retain the original information and cannot be completely flattened
            const _flatCellValue = (0, utils_2.isTextBaseType)(entityField.type) ? flatCellValue : flatCellValue === null || flatCellValue === void 0 ? void 0 : flatCellValue.flat(1);
            switch (this.rollUpType) {
                case field_types_1.RollUpFuncType.VALUES:
                case field_types_1.RollUpFuncType.ARRAYJOIN:
                case field_types_1.RollUpFuncType.CONCATENATE:
                    return _flatCellValue;
                case field_types_1.RollUpFuncType.ARRAYUNIQUE:
                    return (0, lodash_1.uniqWith)(_flatCellValue, (cv1, cv2) => field_1.Field.bindContext(entityField, this.state).eq(cv1, cv2));
                case field_types_1.RollUpFuncType.ARRAYCOMPACT:
                    return _flatCellValue.filter(v => v !== '' || v != null);
                default:
                    return flatCellValue;
            }
        }
        return flatCellValue;
    }
    // Get the flat lookup cell data
    // For non-text concatenated data,
    getFlatCellValue(recordId, withEmpty) {
        // Starting from the current record, recursively search the lookup tree.
        const recordCellValues = this.getLookUpTreeValue(recordId);
        if ((0, lodash_1.isEmpty)(recordCellValues)) {
            return null;
        }
        const field = new LookUpField(this.field, this.state).getLookUpEntityField();
        if (!field) {
            return null;
        }
        const flatCellValues = [];
        const getRealCellValue = (values) => {
            values.forEach(value => {
                if (value && value.field) {
                    if (value.field.type === field_types_1.FieldType.LookUp) {
                        getRealCellValue(value.cellValue);
                    }
                    else {
                        flatCellValues.push(value.cellValue);
                    }
                }
            });
        };
        getRealCellValue(recordCellValues);
        return withEmpty ? flatCellValues : flatCellValues.filter(item => item !== null);
    }
    /**
     * getLookUpTreeValue getCellvalue Recursively query the data of entity fields and combine them into tree-structured data
     *
     * @param {string} recordId
     * @param {string} [datasheetId]
     * @returns {ILookUpTreeValue[]}
     * @memberof LookUpField
     */
    getLookUpTreeValue(recordId) {
        // The column corresponding to the filter condition is deleted and the result is not displayed
        const { error: isFilterError } = this.checkFilterInfo();
        if (isFilterError) {
            return [];
        }
        const relatedLinkField = this.getRelatedLinkField();
        if (!relatedLinkField) {
            // console.log('Cannot find foreign key field', relatedLinkField);
            return [];
        }
        const { lookUpTargetFieldId, datasheetId, filterInfo, openFilter } = this.field.property;
        const thisSnapshot = (0, selectors_1.getSnapshot)(this.state, datasheetId);
        // IDs of the associated table records
        let recordIDs = store_1.Selectors.getCellValue(this.state, thisSnapshot, recordId, relatedLinkField.id, true, datasheetId, true);
        if (!recordIDs) {
            return [];
        }
        const { foreignDatasheetId } = relatedLinkField.property;
        const foreignSnapshot = (0, selectors_1.getSnapshot)(this.state, foreignDatasheetId);
        if (!foreignSnapshot) {
            return [];
        }
        const lookUpTargetField = this.getLookUpTargetField();
        if (openFilter) {
            // magic reference filter
            recordIDs = new view_filter_derivate_1.ViewFilterDerivate(this.state, foreignDatasheetId).getFilteredRecords({
                linkFieldRecordIds: recordIDs,
                filterInfo,
            });
        }
        return recordIDs && recordIDs.length ? recordIDs.map((recordId) => {
            const cellValue = (0, selectors_1._getLookUpTreeValue)(this.state, foreignSnapshot, recordId, lookUpTargetFieldId, foreignDatasheetId);
            return {
                field: lookUpTargetField,
                recordId,
                cellValue,
                datasheetId: foreignDatasheetId,
            };
        }) : [];
    }
    isEmptyOrNot(operator, cellValue) {
        cellValue = (0, utils_1.handleNullArray)(cellValue);
        switch (operator) {
            /**
             * The logic of isEmpty and isNotEmpty is basically common
             */
            case view_types_1.FOperator.IsEmpty: {
                return cellValue == null;
            }
            case view_types_1.FOperator.IsNotEmpty: {
                return cellValue != null;
            }
        }
    }
    // lookup does not allow editing cells
    recordEditable() {
        return false;
    }
    static getFirstItem(item) {
        if (Array.isArray(item)) {
            return item[0];
        }
        return item;
    }
    /**
     *
     * Compare the size of the two cellValues on the field for sorting
     * The default is to convert to string comparison, if it is not this logic, please implement it yourself
     *
     * @orderInCellValueSensitive {boolean} optional parameter, to determine whether to only do normal sorting for the associated field,
     * without preprocessing the cell content
     *
     * @returns {number} negative => less than, 0 => equal, positive => greater than
     */
    compare(cv1, cv2, orderInCellValueSensitive) {
        const expression = this.getExpression();
        cv1 = (0, utils_1.handleNullArray)(cv1);
        cv2 = (0, utils_1.handleNullArray)(cv2);
        if (expression) {
            switch (this.valueType) {
                case field_types_1.BasicValueType.Number:
                    return number_base_field_1.NumberBaseField._compare(cv1, cv2);
                case field_types_1.BasicValueType.DateTime:
                    return date_time_base_field_1.DateTimeBaseField._compare(cv1, cv2, this.field.property.formatting);
                case field_types_1.BasicValueType.Boolean:
                    return checkbox_field_1.CheckboxField._compare(cv1, cv2, orderInCellValueSensitive);
                default:
                    return super.compare(cv1, cv2, orderInCellValueSensitive);
            }
        }
        const entityField = this.getLookUpEntityField();
        if (!entityField) {
            return 1;
        }
        if (field_1.Field.bindContext(entityField, this.state).basicValueType === field_types_1.BasicValueType.Array) {
            return field_1.Field.bindContext(entityField, this.state).compare(cv1, cv2, orderInCellValueSensitive);
        }
        if (!orderInCellValueSensitive) {
            cv1 = [cv1].flat().sort((a, b) => field_1.Field.bindContext(entityField, this.state).compare(a, b));
            cv2 = [cv2].flat().sort((a, b) => field_1.Field.bindContext(entityField, this.state).compare(a, b));
        }
        const zipCellValue = (0, lodash_1.zip)([cv1].flat(), [cv2].flat());
        let res = 0;
        for (let index = 0; index < zipCellValue.length; index++) {
            const [cv1, cv2] = zipCellValue[index];
            res = field_1.Field.bindContext(entityField, this.state).compare(cv1, cv2);
            if (index === zipCellValue.length - 1) {
                return res;
            }
            if (res === 0) {
                continue;
            }
            return res;
        }
        return res;
    }
    isMeetFilter(operator, cellValue, conditionValue) {
        cellValue = (0, utils_1.handleNullArray)(cellValue);
        const expr = this.getExpression();
        if (expr) {
            switch (this.basicValueType) {
                case field_types_1.BasicValueType.Number:
                    return number_base_field_1.NumberBaseField._isMeetFilter(operator, cellValue, conditionValue);
                case field_types_1.BasicValueType.Boolean:
                    return checkbox_field_1.CheckboxField._isMeetFilter(operator, cellValue, conditionValue);
                case field_types_1.BasicValueType.DateTime:
                    return date_time_base_field_1.DateTimeBaseField._isMeetFilter(operator, cellValue, conditionValue);
                case field_types_1.BasicValueType.String:
                    return text_base_field_1.TextBaseField._isMeetFilter(operator, this.cellValueToString(cellValue), conditionValue);
                case field_types_1.BasicValueType.Array:
                    switch (operator) {
                        // The filter operation of negative semantics requires that each item in the array satisfies
                        case view_types_1.FOperator.DoesNotContain:
                        case view_types_1.FOperator.IsNot:
                        case view_types_1.FOperator.IsEmpty:
                            return cellValue.every(cv => {
                                switch (this.innerBasicValueType) {
                                    case field_types_1.BasicValueType.Number:
                                        return number_base_field_1.NumberBaseField._isMeetFilter(operator, cv, conditionValue);
                                    case field_types_1.BasicValueType.Boolean:
                                        return checkbox_field_1.CheckboxField._isMeetFilter(operator, cv, conditionValue);
                                    case field_types_1.BasicValueType.DateTime:
                                        return date_time_base_field_1.DateTimeBaseField._isMeetFilter(operator, cv, conditionValue);
                                    case field_types_1.BasicValueType.String:
                                        return text_base_field_1.TextBaseField._isMeetFilter(operator, this.cellValueToString(cv), conditionValue);
                                    default:
                                        return false;
                                }
                            });
                        default:
                            return cellValue.some(cv => {
                                switch (this.innerBasicValueType) {
                                    case field_types_1.BasicValueType.Number:
                                        return number_base_field_1.NumberBaseField._isMeetFilter(operator, cv, conditionValue);
                                    case field_types_1.BasicValueType.Boolean:
                                        return checkbox_field_1.CheckboxField._isMeetFilter(operator, cv, conditionValue);
                                    case field_types_1.BasicValueType.DateTime:
                                        return date_time_base_field_1.DateTimeBaseField._isMeetFilter(operator, cv, conditionValue);
                                    case field_types_1.BasicValueType.String:
                                        return text_base_field_1.TextBaseField._isMeetFilter(operator, this.cellValueToString(cv), conditionValue);
                                    default:
                                        return false;
                                }
                            });
                    }
            }
        }
        const entityField = this.getLookUpEntityField();
        if (!entityField) {
            return false;
        }
        const judge = (cv) => {
            return field_1.Field.bindContext(entityField, this.state).isMeetFilter(operator, cv, conditionValue);
        };
        // The cv of the lookup is already the value after flat, and the original field value is an array, using common comparison logic.
        if (field_1.Field.bindContext(entityField, this.state).basicValueType === field_types_1.BasicValueType.Array && operator !== view_types_1.FOperator.IsRepeat) {
            // Due to the existence of the "me" filter item, cellValue and conditionValue are not congruent,
            // For Member type directly use isMeetFilter for calculation, it will convert the 'Self' tag into the corresponding UnitId for comparison
            if (entityField.type === field_types_1.FieldType.Member) {
                return field_1.Field.bindContext(entityField, this.state).isMeetFilter(operator, cellValue, conditionValue);
            }
            switch (operator) {
                case view_types_1.FOperator.Is:
                    return field_1.Field.bindContext(entityField, this.state).eq(cellValue, conditionValue);
                case view_types_1.FOperator.IsNot:
                    return !field_1.Field.bindContext(entityField, this.state).eq(cellValue, conditionValue);
                default:
                    return field_1.Field.bindContext(entityField, this.state).isMeetFilter(operator, cellValue, conditionValue);
            }
        }
        switch (operator) {
            // The `filter` operation of negative semantics requires that each item in the array satisfies
            case view_types_1.FOperator.DoesNotContain:
            case view_types_1.FOperator.IsNot:
            case view_types_1.FOperator.IsEmpty:
                return Array.isArray(cellValue) ? cellValue
                    .every(cv => judge(cv)) : judge(cellValue);
            default:
                return Array.isArray(cellValue) ?
                    cellValue.some(cv => judge(cv)) : judge(cellValue);
        }
    }
    cellValueToString(cellValue, _options) {
        var _a;
        if (cellValue == null) {
            return null;
        }
        const rollUpType = this.rollUpType;
        if (!exports.ORIGIN_VALUES_FUNC_SET.has(rollUpType) && !exports.LOOKUP_VALUE_FUNC_SET.has(rollUpType)) {
            try {
                switch (this.basicValueType) {
                    case field_types_1.BasicValueType.Number:
                    case field_types_1.BasicValueType.Boolean: {
                        return (0, number_base_field_1.numberFormat)(cellValue, (_a = this.field.property) === null || _a === void 0 ? void 0 : _a.formatting);
                    }
                    case field_types_1.BasicValueType.DateTime:
                        return (0, date_time_base_field_1.dateTimeFormat)(cellValue, this.field.property.formatting, (0, selectors_1.getUserTimeZone)(this.state));
                    case field_types_1.BasicValueType.String:
                    case field_types_1.BasicValueType.Array:
                        return String(cellValue);
                }
            }
            catch (error) {
                return 'compute error';
            }
        }
        return this.arrayValueToString(this.cellValueToArray(cellValue));
    }
    cellValueToArray(cellValue) {
        // cellValue = handleNullArray(cellValue);
        const entityField = this.getLookUpEntityField();
        if (!entityField) {
            return null;
        }
        const basicValueType = field_1.Field.bindContext(entityField, this.state).basicValueType;
        return cellValue == null ? null : cellValue.reduce((result, value) => {
            // number|boolean|datetime type does not need any conversion
            if (basicValueType === field_types_1.BasicValueType.Number ||
                basicValueType === field_types_1.BasicValueType.Boolean ||
                basicValueType === field_types_1.BasicValueType.DateTime) {
                result.push(value);
                return result;
            }
            if ((0, utils_2.isTextBaseType)(entityField.type)) {
                result.push(field_1.Field.bindContext(entityField, this.state).cellValueToString(value));
                return result;
            }
            // BasicValueType.Array & value is of Array type and needs to be processed
            if (Array.isArray(value) || (value != null && basicValueType === field_types_1.BasicValueType.Array)) {
                [value].flat(Infinity).forEach(v => {
                    result.push(field_1.Field.bindContext(entityField, this.state).cellValueToString([v]));
                });
                return result;
            }
            result.push(field_1.Field.bindContext(entityField, this.state).cellValueToString(value));
            return result;
        }, []);
    }
    arrayValueToString(cellValue, options) {
        const rollUpType = this.field.property.rollUpType;
        let vArray = this.arrayValueToArrayStringValueArray(cellValue, options);
        // Process ARRAYUNIQUE, ARRAYCOMPACT functions
        if (rollUpType === field_types_1.RollUpFuncType.ARRAYUNIQUE) {
            vArray = vArray && [...new Set(vArray)];
        }
        if (rollUpType === field_types_1.RollUpFuncType.ARRAYCOMPACT) {
            vArray = vArray && vArray.filter(v => v !== '');
        }
        if (rollUpType === field_types_1.RollUpFuncType.CONCATENATE) {
            return vArray == null ? null : vArray.join('');
        }
        return vArray == null ? null : vArray.join(', ');
    }
    arrayValueToArrayStringValueArray(cellValue, _options) {
        cellValue = (0, utils_1.handleNullArray)(cellValue);
        const entityField = this.getLookUpEntityField();
        if (!entityField) {
            return null;
        }
        const basicValueType = field_1.Field.bindContext(entityField, this.state).basicValueType;
        return cellValue == null ? null : cellValue.map(value => {
            if (value == null) {
                return null;
            }
            // Date type should use the format configured by the lookup field
            if (basicValueType === field_types_1.BasicValueType.DateTime) {
                const formatting = this.field.property.formatting || entityField.property;
                return (0, date_time_base_field_1.dateTimeFormat)(value, formatting, (0, selectors_1.getUserTimeZone)(this.state));
            }
            // The number|boolean type should use the format configured by the lookup field
            if (basicValueType === field_types_1.BasicValueType.Number || basicValueType === field_types_1.BasicValueType.Boolean) {
                if (!exports.NOT_FORMAT_FUNC_SET.has(this.rollUpType)) {
                    const property = this.field.property.formatting || entityField.property;
                    return (0, number_base_field_1.numberFormat)(value, property);
                }
                return field_1.Field.bindContext(entityField, this.state).cellValueToString(Number(value));
            }
            return String(value);
        }).filter(i => i != null);
    }
    cellValueToStdValue(cellValue) {
        cellValue = (0, utils_1.handleNullArray)(cellValue);
        const stdValue = {
            sourceType: this.field.type,
            data: [],
        };
        if (cellValue != null) {
            stdValue.data.push({ text: this.cellValueToString(cellValue) || '' });
        }
        return stdValue;
    }
    stdValueToCellValue(stdValue) {
        const lookUpEntityField = this.getLookUpEntityField();
        if (!lookUpEntityField) {
            return null;
        }
        const lookUpEntityFieldInstance = field_1.Field.bindContext(lookUpEntityField, this.state);
        return lookUpEntityFieldInstance.stdValueToCellValue(stdValue);
    }
    validate(value) {
        return value == null;
    }
    /**
     * Whether to multi-select fields of value type.
     * For multiple selections, the collaborator field will return true.
     * NOTE: Fields that are guaranteed to return true, value is an array.
     */
    isMultiValueField() {
        return true;
    }
    defaultValueForCondition(condition) {
        switch (this.valueType) {
            case field_types_1.BasicValueType.DateTime:
                return date_time_base_field_1.DateTimeBaseField._defaultValueForCondition(condition);
            default:
                return null;
        }
    }
    /**
     * Returns the default value of the field attribute configuration when adding a record
     */
    defaultValue() {
        return null;
    }
    /**
     * @description convert the statistical parameters into Chinese
     */
    statType2text(type) {
        const expression = this.getExpression();
        if (expression) {
            switch (this.basicValueType) {
                case field_types_1.BasicValueType.Number:
                case field_types_1.BasicValueType.Boolean:
                case field_types_1.BasicValueType.String:
                case field_types_1.BasicValueType.Array:
                    return stat_1.StatTranslate[type];
                case field_types_1.BasicValueType.DateTime:
                    return date_time_base_field_1.DateTimeBaseField._statType2text(type);
            }
        }
        const lookUpEntityField = field_1.Field.bindContext(this.field, this.state).getLookUpEntityField();
        if (!lookUpEntityField) {
            return stat_1.StatTranslate[type];
        }
        return field_1.Field.bindContext(lookUpEntityField, this.state).statType2text(type);
    }
    // Get the fields that the lookup field depends on, and only get one level of relationship
    getCurrentDatasheetRelatedFieldKeys(datasheetId) {
        const { relatedLinkFieldId, lookUpTargetFieldId, filterInfo, openFilter } = this.field.property;
        const allKeys = [];
        // Depends on the link field of the current table
        allKeys.push(`${datasheetId}-${relatedLinkFieldId}`);
        const relatedLinkField = this.getRelatedLinkField();
        if (relatedLinkField) {
            const { foreignDatasheetId } = relatedLinkField.property;
            allKeys.push(`${foreignDatasheetId}-${lookUpTargetFieldId}`);
            // filter field triggers magic app update
            if (openFilter && filterInfo) {
                const { conditions } = filterInfo;
                conditions.forEach(condition => {
                    allKeys.push(`${foreignDatasheetId}-${condition.fieldId}`);
                });
            }
        }
        return allKeys;
    }
    cellValueToApiStandardValue(cellValue) {
        cellValue = (0, utils_1.handleNullArray)(cellValue);
        const entityField = this.getLookUpEntityField();
        if (!entityField || !cellValue) {
            return null;
        }
        if (Array.isArray(cellValue)) {
            const targetField = field_1.Field.bindContext(entityField, this.state);
            if (targetField.basicValueType === field_types_1.BasicValueType.Array) {
                return targetField.cellValueToApiStandardValue(cellValue);
            }
            const result = [];
            cellValue.forEach(item => {
                const value = targetField.cellValueToApiStandardValue(item);
                if (value != null) {
                    result.push(value);
                }
            });
            if (result.length) {
                return result;
            }
            return null;
        }
        return cellValue;
    }
    cellValueToApiStringValue(cellValue) {
        cellValue = (0, utils_1.handleNullArray)(cellValue);
        const entityField = this.getLookUpEntityField();
        if (!entityField) {
            return null;
        }
        if (entityField.type == field_types_1.FieldType.Member || entityField.type == field_types_1.FieldType.CreatedBy || entityField.type == field_types_1.FieldType.LastModifiedBy) {
            return field_1.Field.bindContext(entityField, this.state).cellValueToApiStringValue(cellValue);
        }
        return this.cellValueToString(cellValue);
    }
    cellValueToOpenValue(cellValue) {
        var _a;
        if (cellValue == null) {
            return null;
        }
        const entityField = this.getLookUpEntityField();
        if (!entityField) {
            return null;
        }
        const expression = this.getExpression();
        if (expression) {
            // When there is an expression, the output value type is the basic type, which can be output directly
            switch (this.valueType) {
                case field_types_1.BasicValueType.Number:
                case field_types_1.BasicValueType.Boolean:
                case field_types_1.BasicValueType.DateTime:
                case field_types_1.BasicValueType.String:
                default:
                    return cellValue;
            }
        }
        // These two are functions that convert array to string.
        if (exports.LOOKUP_VALUE_FUNC_SET.has(this.rollUpType)) {
            return this.cellValueToString(cellValue);
        }
        // What comes in here is a one-dimensional array, first compatible with a two-dimensional array.
        if (Array.isArray(cellValue)) {
            const targetField = field_1.Field.bindContext(entityField, this.state);
            // TODO is temporarily compatible with the data structure (wait for getCellValue to be transformed to return an unflattened array)
            const isDoubleArray = ((_a = this.openValueJsonSchema.items) === null || _a === void 0 ? void 0 : _a.type) === 'array';
            // The expected two-dimensional array has been flattened at the time of cv, 
            // and it is packaged here and upgraded to a two-dimensional array. 
            // Now it is only a structural compatibility, which will affect the actual business logic.
            // In the robot scene, the values input by the UI are currently spliced into strings, so it doesn't matter much.
            const _cellValue = isDoubleArray ? [cellValue.filter(cv => cv != null)] : cellValue;
            const result = _cellValue
                .map(item => targetField.cellValueToOpenValue(item));
            if (result.length) {
                return result;
            }
            return null;
        }
        return cellValue;
    }
    // TODO
    openWriteValueToCellValue() {
        return null;
    }
    get openFieldProperty() {
        const res = {
            relatedLinkFieldId: this.field.property.relatedLinkFieldId,
            targetFieldId: this.field.property.lookUpTargetFieldId,
            rollupFunction: this.rollUpType,
        };
        if (this.hasError) {
            res.hasError = this.hasError;
            return res;
        }
        res.valueType = this.basicValueType;
        const lookUpEntityFieldInfo = this.getLookUpEntityFieldInfo();
        if (lookUpEntityFieldInfo) {
            res.entityField = {
                datasheetId: lookUpEntityFieldInfo.datasheetId,
                field: field_1.Field.bindContext(lookUpEntityFieldInfo.field, this.state).getOpenField(lookUpEntityFieldInfo.datasheetId)
            };
        }
        if (this.apiMetaPropertyFormat) {
            res.format = this.apiMetaPropertyFormat;
        }
        return res;
    }
    validateUpdateOpenProperty(updateProperty) {
        return LookUpField.openUpdatePropertySchema.validate(updateProperty);
    }
    updateOpenFieldPropertyTransformProperty(openFieldProperty) {
        const { relatedLinkFieldId, targetFieldId: lookUpTargetFieldId, rollupFunction: rollUpType, format } = openFieldProperty;
        const formatting = format ? (0, utils_1.computedFormattingToFormat)(format) : undefined;
        return {
            datasheetId: this.field.property.datasheetId,
            relatedLinkFieldId,
            lookUpTargetFieldId,
            rollUpType,
            formatting
        };
    }
    filterValueToOpenFilterValue(value) {
        if (this.getExpression()) {
            switch (this.valueType) {
                case field_types_1.BasicValueType.Number:
                    return number_base_field_1.NumberBaseField._filterValueToOpenFilterValue(value);
                case field_types_1.BasicValueType.Boolean:
                    return checkbox_field_1.CheckboxField._filterValueToOpenFilterValue(value);
                case field_types_1.BasicValueType.String:
                    return text_base_field_1.TextBaseField._filterValueToOpenFilterValue(value);
                case field_types_1.BasicValueType.DateTime:
                    return date_time_base_field_1.DateTimeBaseField._filterValueToOpenFilterValue(value);
            }
        }
        const lookUpEntityFieldInfo = this.getLookUpEntityFieldInfo();
        if (!lookUpEntityFieldInfo) {
            return null;
        }
        const entityField = field_1.Field.bindContext(lookUpEntityFieldInfo.field, this.state);
        return entityField.filterValueToOpenFilterValue(value);
    }
    openFilterValueToFilterValue(value) {
        if (this.getExpression()) {
            switch (this.valueType) {
                case field_types_1.BasicValueType.Number:
                    return number_base_field_1.NumberBaseField._openFilterValueToFilterValue(value);
                case field_types_1.BasicValueType.Boolean:
                    return checkbox_field_1.CheckboxField._openFilterValueToFilterValue(value);
                case field_types_1.BasicValueType.String:
                    return text_base_field_1.TextBaseField._openFilterValueToFilterValue(value);
                case field_types_1.BasicValueType.DateTime:
                    return date_time_base_field_1.DateTimeBaseField._openFilterValueToFilterValue(value);
            }
        }
        const lookUpEntityFieldInfo = this.getLookUpEntityFieldInfo();
        if (!lookUpEntityFieldInfo) {
            return null;
        }
        const entityField = field_1.Field.bindContext(lookUpEntityFieldInfo.field, this.state);
        return entityField.openFilterValueToFilterValue(value);
    }
    validateOpenFilterValue(value) {
        if (this.getExpression()) {
            switch (this.valueType) {
                case field_types_1.BasicValueType.Number:
                    return number_base_field_1.NumberBaseField._validateOpenFilterValue(value);
                case field_types_1.BasicValueType.Boolean:
                    return checkbox_field_1.CheckboxField._validateOpenFilterValue(value);
                case field_types_1.BasicValueType.String:
                    return text_base_field_1.TextBaseField._validateOpenFilterValue(value);
                case field_types_1.BasicValueType.DateTime:
                    return date_time_base_field_1.DateTimeBaseField._validateOpenFilterValue(value);
            }
        }
        const lookUpEntityFieldInfo = this.getLookUpEntityFieldInfo();
        if (!lookUpEntityFieldInfo) {
            return (0, validate_schema_1.joiErrorResult)(`${this.field.name} look up has no entity field`);
        }
        const entityField = field_1.Field.bindContext(lookUpEntityFieldInfo.field, this.state);
        return entityField.openFilterValueToFilterValue(value);
    }
}
exports.LookUpField = LookUpField;
LookUpField.propertySchema = joi_1.default.object({
    datasheetId: (0, validate_schema_1.datasheetIdString)().required(),
    relatedLinkFieldId: joi_1.default.string().pattern(/^fld.+/, 'fieldId').required(),
    lookUpTargetFieldId: joi_1.default.string().pattern(/^fld.+/, 'fieldId').required(),
    rollUpType: joi_1.default.valid(...(0, validate_schema_1.enumToArray)(field_types_1.RollUpFuncType)),
    formatting: (0, validate_schema_1.computedFormatting)(),
    filterInfo: joi_1.default.object({
        conjunction: joi_1.default.valid(...(0, validate_schema_1.enumToArray)(view_types_1.FilterConjunction)).required(),
        conditions: joi_1.default.array().items(joi_1.default.object({
            conditionId: joi_1.default.string().required(),
            fieldId: joi_1.default.string().pattern(/^fld.+/, 'fieldId').required(),
            operator: joi_1.default.valid(...(0, validate_schema_1.enumToArray)(view_types_1.FOperator)).required(),
            fieldType: joi_1.default.valid(...(0, validate_schema_1.enumToArray)(field_types_1.FieldType)).required(),
            value: joi_1.default.any(),
        }))
    }),
    openFilter: joi_1.default.boolean(),
}).required();
LookUpField.openUpdatePropertySchema = joi_1.default.object({
    relatedLinkFieldId: joi_1.default.string().pattern(/^fld.+/, 'fieldId').required(),
    targetFieldId: joi_1.default.string().pattern(/^fld.+/, 'fieldId').required(),
    rollupFunction: joi_1.default.valid(...(0, validate_schema_1.enumToArray)(field_types_1.RollUpFuncType)),
    format: (0, validate_schema_1.computedFormattingStr)(),
});
//# sourceMappingURL=lookup_field.js.map